import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";

export function recordDate(value: string | null | undefined) {
  if (!value) return "Not recorded";
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-PH", { day: "numeric", month: "short", year: "numeric" });
}

export function RecordStatus({ value, label }: { value: string; label?: string }) {
  const tone = ["approved", "valid", "verified"].includes(value) ? "good" : ["rejected", "revoked"].includes(value) ? "bad" : ["pending", "expired"].includes(value) ? "warn" : "neutral";
  return <span className={`customer-record-status ${tone}`}>{label || value.replace(/_/g, " ").replace(/^./, c => c.toUpperCase())}</span>;
}

export function RecordFacts({ facts }: { facts: [string, ReactNode][] }) {
  return <dl className="customer-record-facts">{facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || "Not recorded"}</dd></div>)}</dl>;
}

export function RecordTable({ columns, rows, selectedId, onToggle, renderDetail, disabled = false }: {
  columns: string[]; rows: { id: string; title: string; subtitle?: string; cells: ReactNode[] }[];
  selectedId: string | null; onToggle: (id: string) => void; renderDetail: (id: string) => ReactNode; disabled?: boolean;
}) {
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  function close(id: string) { onToggle(id); triggers.current.get(id)?.focus(); }
  return <div className="customer-record-table-scroll"><table className="customer-record-table">
    <thead><tr>{columns.map(label => <th scope="col" key={label}>{label}</th>)}<th scope="col">Details</th></tr></thead>
    <tbody>{rows.map(row => {
      const expanded = selectedId === row.id;
      const panelId = `customer-record-${row.id}`;
      return <Fragment key={row.id}>
        <tr className={`customer-record-row ${expanded ? "expanded" : ""}`} onClick={() => { if (!disabled) onToggle(row.id); }}>
          <th scope="row"><button type="button" disabled={disabled} ref={node => { if (node) triggers.current.set(row.id, node); else triggers.current.delete(row.id); }}
            aria-expanded={expanded} aria-controls={panelId} onClick={event => { event.stopPropagation(); onToggle(row.id); }}>
            {row.title}{row.subtitle && <small>{row.subtitle}</small>}
          </button></th>
          {row.cells.map((cell, index) => <td key={index}>{cell}</td>)}
          <td><span className="customer-record-disclosure" aria-hidden="true">{expanded ? "Collapse" : "View details"}{expanded ? <ChevronDown /> : <ChevronRight />}</span></td>
        </tr>
        {expanded && <tr><td colSpan={columns.length + 1} className="customer-record-detail-cell"><section id={panelId} aria-label={`${row.title} details`} className="customer-record-detail">
          <button type="button" disabled={disabled} className="customer-record-close" aria-label={`Collapse ${row.title} details`} onClick={() => close(row.id)}><X size={16} /></button>
          {renderDetail(row.id)}
        </section></td></tr>}
      </Fragment>;
    })}</tbody>
  </table></div>;
}

export function RecordPagination({ offset, total, onPage, disabled = false }: { offset: number; total: number; onPage: (offset: number) => void; disabled?: boolean }) {
  if (total <= 50) return null;
  return <div className="customer-record-pagination"><button type="button" className="btn-outline h-8 px-3" disabled={disabled || offset === 0} onClick={() => onPage(Math.max(0, offset - 50))}>Previous</button>
    <span>{offset + 1}–{Math.min(offset + 50, total)} of {total}</span>
    <button type="button" className="btn-outline h-8 px-3" disabled={disabled || offset + 50 >= total} onClick={() => onPage(offset + 50)}>Next</button></div>;
}

export function RecordEvidenceDialog({ evidence, onClose, className = "" }: { evidence: { url: string; label: string; contentType?: string; returnFocus?: HTMLElement | null }; onClose: () => void; className?: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    const trigger = evidence.returnFocus ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    element?.showModal();
    return () => { if (element?.open) element.close(); if (trigger?.isConnected) trigger.focus(); };
  }, [evidence.returnFocus]);
  return <dialog ref={dialog} aria-label={evidence.label} className={`customer-record-evidence ${className}`} onCancel={event => { event.preventDefault(); onClose(); }}>
    <header><h3>{evidence.label}</h3><button type="button" autoFocus aria-label="Close document preview" className="customer-record-close" onClick={onClose}><X size={18} /></button></header>
    {evidence.contentType === "application/pdf" ? <iframe title={evidence.label} src={evidence.url} /> : <img src={evidence.url} alt={evidence.label} />}
  </dialog>;
}
